import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Portal } from 'react-portal'

import { HighlightedText } from '@sketch/components'

import { getCommandBarRenderState, getVisibleCommandBarItems } from '../utils'
import { CommandBarItem } from '../types'

import * as S from './CommandBar.styles'

import { ReactComponent as SearchIcon } from '@sketch/icons/img-magnifying-glass-17'

const stopNextEnterKeyUp = () => {
  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.key !== 'Enter') {
      return
    }

    event.preventDefault()
    event.stopPropagation()
  }

  window.addEventListener('keyup', handleKeyUp, {
    capture: true,
    once: true,
  })
}

interface CommandBarProps {
  isOpen: boolean
  items: CommandBarItem[]
  loading: boolean
  error?: Error
  query: string
  onQueryChange: (query: string) => void
  onClose: () => void
  onRunItem: (item: CommandBarItem, rank: number) => void
}

export const CommandBar = ({
  isOpen,
  items,
  loading,
  error,
  query,
  onQueryChange,
  onClose,
  onRunItem,
}: CommandBarProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const resultItemRefs = useRef<Array<HTMLButtonElement | null>>([])
  const hasMouseMovedOverResultsRef = useRef(false)
  const hasUserSelectedItemRef = useRef(false)
  const previousQueryRef = useRef(query)
  const latestRenderedItemsRef = useRef<CommandBarItem[]>([])
  const [activeItemId, setActiveItemId] = useState<string | null>(null)

  const visibleItems = useMemo(
    () => getVisibleCommandBarItems(items, query),
    [items, query]
  )

  const { renderedItemIds, renderedItems, sections } = useMemo(() => {
    return getCommandBarRenderState(visibleItems, query)
  }, [query, visibleItems])

  const activeIndex = useMemo(() => {
    if (!activeItemId) {
      return 0
    }

    const index = renderedItems.findIndex(item => item.id === activeItemId)
    return index === -1 ? 0 : index
  }, [activeItemId, renderedItems])

  latestRenderedItemsRef.current = renderedItems

  // Reset selection before the command bar paints on a fresh open, so the last
  // selection from the previous session never briefly appears.
  useLayoutEffect(() => {
    if (!isOpen) {
      return
    }

    hasUserSelectedItemRef.current = false
    previousQueryRef.current = ''
    setActiveItemId(latestRenderedItemsRef.current[0]?.id || null)

    return () => {
      hasUserSelectedItemRef.current = false
      setActiveItemId(null)
    }
  }, [isOpen])

  // Opening should focus the search field without letting a stationary cursor
  // over the results immediately steal the active keyboard selection.
  useEffect(() => {
    if (isOpen) {
      hasMouseMovedOverResultsRef.current = false
      window.setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [isOpen])

  // Preserve keyboard selection across async result updates, but reset to the
  // first result when the user intentionally changes the query.
  useEffect(() => {
    const firstItemId = renderedItems[0]?.id || null

    if (previousQueryRef.current !== query) {
      previousQueryRef.current = query
      hasUserSelectedItemRef.current = false
      setActiveItemId(firstItemId)
      return
    }

    if (query === '' && !hasUserSelectedItemRef.current) {
      setActiveItemId(firstItemId)
      return
    }

    setActiveItemId(currentItemId => {
      if (
        currentItemId &&
        renderedItems.some(item => item.id === currentItemId)
      ) {
        return currentItemId
      }

      return firstItemId
    })
  }, [query, renderedItemIds, renderedItems])

  // Keyboard navigation should keep the active row visible as the user moves
  // through a scrollable result list.
  useEffect(() => {
    const activeItem = resultItemRefs.current[activeIndex]

    if (!activeItem) {
      return
    }

    activeItem.scrollIntoView({
      block: 'nearest',
    })
  }, [activeIndex])

  if (!isOpen) {
    return null
  }

  const runActiveItem = () => {
    const item = renderedItems[activeIndex]

    if (!item || item.disabledReason) {
      return
    }

    onRunItem(item, activeIndex)
  }

  const updateActiveItem = (getNextIndex: (index: number) => number) => {
    const nextIndex = getNextIndex(activeIndex)
    setActiveItemId(renderedItems[nextIndex]?.id || null)
    hasUserSelectedItemRef.current = true
  }

  const handleResultItemMouseEnter = (index: number) => {
    if (!hasMouseMovedOverResultsRef.current) {
      return
    }

    updateActiveItem(() => index)
  }

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (renderedItems.length === 0) {
        return
      }
      updateActiveItem(index => Math.min(index + 1, renderedItems.length - 1))
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (renderedItems.length === 0) {
        return
      }
      updateActiveItem(index => Math.max(index - 1, 0))
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      stopNextEnterKeyUp()
      runActiveItem()
    }
  }

  let itemIndex = -1

  return (
    <Portal>
      <S.Overlay onMouseDown={onClose}>
        <S.Dialog
          role="dialog"
          aria-modal="true"
          aria-label="Command bar"
          onMouseDown={event => event.stopPropagation()}
        >
          <S.SearchRow>
            <S.SearchIconWrapper>
              <SearchIcon />
            </S.SearchIconWrapper>
            <S.Input
              ref={inputRef}
              aria-label="Search commands"
              placeholder="Search commands, documents, projects"
              value={query}
              onChange={event => onQueryChange(event.target.value)}
              onKeyDown={handleInputKeyDown}
            />
          </S.SearchRow>

          <S.Results
            onMouseMove={() => {
              hasMouseMovedOverResultsRef.current = true
            }}
          >
            {sections.map(([section, sectionItems]) => (
              <S.Section key={section}>
                <S.SectionTitle>{section}</S.SectionTitle>
                {sectionItems.map(item => {
                  itemIndex += 1
                  const currentIndex = itemIndex
                  const Icon = item.icon
                  const iconNode = item.iconNode

                  return (
                    <S.ResultItem
                      key={item.id}
                      ref={element => {
                        resultItemRefs.current[currentIndex] = element
                      }}
                      $active={currentIndex === activeIndex}
                      disabled={Boolean(item.disabledReason)}
                      onMouseEnter={() =>
                        handleResultItemMouseEnter(currentIndex)
                      }
                      onClick={() => onRunItem(item, currentIndex)}
                    >
                      <S.ResultIcon>
                        {iconNode || (Icon && <Icon />)}
                      </S.ResultIcon>
                      <S.ResultText>
                        <S.ResultTitle>
                          <HighlightedText search={query}>
                            {item.title}
                          </HighlightedText>
                        </S.ResultTitle>
                        {item.subtitle && (
                          <S.ResultSubtitle>
                            <HighlightedText search={query}>
                              {item.subtitle}
                            </HighlightedText>
                          </S.ResultSubtitle>
                        )}
                        {item.disabledReason && (
                          <S.ResultSubtitle>
                            {item.disabledReason}
                          </S.ResultSubtitle>
                        )}
                      </S.ResultText>
                      {item.shortcut && (
                        <S.Shortcut>{item.shortcut}</S.Shortcut>
                      )}
                    </S.ResultItem>
                  )
                })}
              </S.Section>
            ))}

            {loading && <S.StateMessage>Searching...</S.StateMessage>}
            {error && (
              <S.StateMessage>
                Some remote results could not be loaded.
              </S.StateMessage>
            )}
            {!loading && visibleItems.length === 0 && (
              <S.StateMessage>No results</S.StateMessage>
            )}
          </S.Results>
        </S.Dialog>
      </S.Overlay>
    </Portal>
  )
}
